import {
  getAmpMessage,
  getAmpRawXml,
  listAmpMessages,
  type AmpDetail,
  type AmpListItem,
  type AmpListQuery,
} from '../api/amp';
import { StatusBadge } from '../components/StatusBadge';
import { CorrelationIdField, CustomsMessageConsole } from '../components/CustomsMessageConsole';
import { formatValidationIssue, type ValidationReportSection } from '../lib/customsMessages';

const PAGE_SIZE = 20;

const initialFilters: AmpListQuery = {
  page: 1,
  pageSize: PAGE_SIZE,
  ampNumber: '',
  partnerCode: '',
  authorizationType: '',
  functionCode: '',
  processingStatus: '',
};

function validationSections(detail: AmpDetail): ValidationReportSection[] {
  const report = detail.validationReport;
  if (!report) return [];
  return [
    { label: 'Errors', tone: 'error', items: report.errors.map(formatValidationIssue) },
    { label: 'Warnings', tone: 'warning', items: report.warnings.map(formatValidationIssue) },
  ];
}

export function AmpPage() {
  return (
    <CustomsMessageConsole<AmpListItem, AmpListQuery, AmpDetail>
      eyebrow="Maritime"
      title="AMP — AMPI / AMPE"
      purpose="Inspect ADII AMP (traceAMP) authorization messages received directly over HTTP — AMPI (import) and AMPE (export) — their normalized JSON, validation results and processing history. This channel is a direct synchronous HTTP ingestion, distinct from the AS2 transport used by other customs messages; it never traverses the AS2 gateway, so it will not appear on the AS2 inbound/outbound pages."
      status="PRODUCTION_READY"
      next="Read-only in this release; reprocess/replay actions are a planned follow-up, matching Tryptique and Etat de Chargement."
      listPanelTitle="AMP messages"
      primaryColumnHeader="AMP number"
      emptyRowLabel="No AMP message matches the current filters."
      detailTitle="AMP detail"
      initialFilters={initialFilters}
      pageSize={PAGE_SIZE}
      listFetch={listAmpMessages}
      detailFetch={getAmpMessage}
      rawXmlFetch={getAmpRawXml}
      getRowId={(item) => item.id}
      getRowLabel={(item) => item.ampNumber ?? item.id}
      filterFields={(filters, patchFilters) => (
        <>
          <label className="filter-field">
            <span>AMP number</span>
            <input value={filters.ampNumber ?? ''} onChange={(event) => patchFilters({ ampNumber: event.target.value })} />
          </label>
          <label className="filter-field">
            <span>Partner</span>
            <input value={filters.partnerCode ?? ''} onChange={(event) => patchFilters({ partnerCode: event.target.value })} />
          </label>
          <label className="filter-field">
            <span>Type</span>
            <select value={filters.authorizationType ?? ''} onChange={(event) => patchFilters({ authorizationType: event.target.value as AmpListQuery['authorizationType'] })}>
              <option value="">All</option>
              <option value="AMPI">AMPI (import)</option>
              <option value="AMPE">AMPE (export)</option>
              <option value="AMI">AMI</option>
              <option value="UNKNOWN">Unknown</option>
            </select>
          </label>
          <label className="filter-field">
            <span>Function</span>
            <select value={filters.functionCode ?? ''} onChange={(event) => patchFilters({ functionCode: event.target.value as AmpListQuery['functionCode'] })}>
              <option value="">All</option>
              <option value="CREATION">Creation</option>
              <option value="MODIFICATION">Modification</option>
              <option value="CANCELLATION">Cancellation</option>
              <option value="UNKNOWN">Unknown</option>
            </select>
          </label>
          <label className="filter-field">
            <span>Processing status</span>
            <select value={filters.processingStatus ?? ''} onChange={(event) => patchFilters({ processingStatus: event.target.value })}>
              <option value="">All</option>
              <option value="PROCESSED">Processed</option>
              <option value="VALID_WITH_WARNINGS">Valid with warnings</option>
              <option value="REPLAYED">Replayed</option>
              <option value="REJECTED">Rejected</option>
              <option value="QUARANTINED">Quarantined</option>
              <option value="FAILED">Failed</option>
            </select>
          </label>
        </>
      )}
      listColumns={[
        { header: 'Type', render: (item) => item.authorizationType },
        { header: 'Function', render: (item) => item.functionCode ?? 'n/a' },
        { header: 'Partner', render: (item) => item.partnerCode ?? 'n/a' },
        { header: 'Validation', render: (item) => <StatusBadge value={item.validationStatus} /> },
        { header: 'Processing', render: (item) => <StatusBadge value={item.processingStatus} /> },
        { header: 'Received', render: (item) => new Date(item.receivedAt).toLocaleString('en-GB') },
      ]}
      detailSummaryFields={(data) => (
        <>
          <div><strong>Type</strong><span>{data.authorizationType}</span></div>
          <div><strong>Classification</strong><span>{data.classificationStatus}</span></div>
          <div><strong>Function</strong><span>{data.functionCode ?? 'n/a'}</span></div>
          <div><strong>Root element</strong><span>{data.rootElement}</span></div>
          <div><strong>Filename</strong><span className="mono">{data.filename ?? 'n/a'}</span></div>
          <div><strong>Partner</strong><span>{data.partnerCode ?? 'n/a'}</span></div>
          <div><strong>AMP number</strong><span className="mono">{data.ampNumber ?? 'n/a'}</span></div>
          <div><strong>Source message number</strong><span className="mono">{data.sourceMessageNumber ?? 'n/a'}</span></div>
          <div><strong>Sender</strong><span>{data.sender ?? 'n/a'}</span></div>
          <div><strong>Receiver</strong><span>{data.receiver ?? 'n/a'}</span></div>
          <div><strong>Validation</strong><span><StatusBadge value={data.validationStatus} /></span></div>
          <div><strong>Processing</strong><span><StatusBadge value={data.processingStatus} /></span></div>
          <CorrelationIdField value={data.correlationId} />
        </>
      )}
      detailValidationReport={validationSections}
    />
  );
}
